import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { AIChatPlugin, AIPlugin } from '@alpod-ai-writer/plate-plugins/ai/react';
import { CitationInputPlugin, CitationPlugin } from '@alpod-ai-writer/plate-plugins/citations/react';
import { FigureCaptionPlugin } from '@alpod-ai-writer/plate-plugins/figure-caption/react';
import { FootnotesPlugin } from '@alpod-ai-writer/plate-plugins/footnotes/react';
import { LinkPlugin } from '@alpod-ai-writer/plate-plugins/link/react';
import { ImagePlugin, PlaceholderPlugin } from '@alpod-ai-writer/plate-plugins/media/react';
import { NodeIdPlugin } from '@alpod-ai-writer/plate-plugins/node-id';
import { ShortcutInputPlugin, ShortcutPlugin } from '@alpod-ai-writer/plate-plugins/shortcuts/react';
import { SourceCaptionPlugin } from '@alpod-ai-writer/plate-plugins/source-caption/react';
import { SuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion/react';
import { TableCellHeaderPlugin, TableCellPlugin, TablePlugin, TableRowPlugin } from '@alpod-ai-writer/plate-plugins/table/react';
import { YjsPlugin } from '@alpod-ai-writer/plate-plugins/yjs';
import type { DocumentConfig } from '@alpod-ai-writer/shared';
import type { CursorData } from '@alpod-ai-writer/shared';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import { cn, withProps } from '@udecode/cn';
import { type SlateElementProps, type TElement } from '@udecode/plate';
import { ParagraphPlugin, PlateElement, PlateLeaf, usePlateEditor } from '@udecode/plate/react';
import { AlignPlugin } from '@udecode/plate-alignment/react';
import { BasicMarksPlugin, BoldPlugin, ItalicPlugin, SubscriptPlugin, SuperscriptPlugin, UnderlinePlugin } from '@udecode/plate-basic-marks/react';
import { SoftBreakPlugin } from '@udecode/plate-break/react';
import { ExitBreakPlugin } from '@udecode/plate-break/react';
import { CaptionPlugin } from '@udecode/plate-caption/react';
import { CommentsPlugin } from '@udecode/plate-comments/react';
import { DndPlugin } from '@udecode/plate-dnd';
import { DocxPlugin } from '@udecode/plate-docx';
import { HEADING_KEYS, HEADING_LEVELS } from '@udecode/plate-heading';
import { HeadingPlugin } from '@udecode/plate-heading/react';
import { HighlightPlugin } from '@udecode/plate-highlight/react';
import { IndentPlugin } from '@udecode/plate-indent/react';
import { ListStyleType } from '@udecode/plate-indent-list';
import { IndentListPlugin } from '@udecode/plate-indent-list/react';
import { JuicePlugin } from '@udecode/plate-juice';
import { BulletedListPlugin, ListItemPlugin, NumberedListPlugin } from '@udecode/plate-list/react';
import { MarkdownPlugin } from '@udecode/plate-markdown';
import { ResetNodePlugin } from '@udecode/plate-reset-node/react';
import { BlockSelectionPlugin } from '@udecode/plate-selection/react';
import { TrailingBlockPlugin } from '@udecode/plate-trailing-block';
import * as Y from 'yjs';

import { AIMenu } from '../components/common/aiMenu.component';
import { BlockDiscussion } from '../components/common/blockDiscussion.component';
import { BlockFootnote } from '../components/common/blockFootnotes.component';
import { BlockSelection } from '../components/common/blockSelection.component';
import { MediaUploadToast } from '../components/common/mediaUploadToast.component';
import { RemoteCursorOverlay } from '../components/common/remoteCursorOverlay.component';
import { AILeaf } from '../components/elements/aiLeaf.component';
import { CitationElement } from '../components/elements/citationElement.component';
import { CitationInputElement } from '../components/elements/citationInputElement.component';
import { CommentLeaf } from '../components/elements/commentLeaf.component';
import { FigureCaptionElement } from '../components/elements/figureCaptionElement.component';
import { FootnoteElement } from '../components/elements/footnoteElement.component';
import { ImageElement } from '../components/elements/imageElement.component';
import { LinkElement } from '../components/elements/linkElement.component';
import { MediaPlaceholderElement } from '../components/elements/mediaPlaceholderElement.component';
import { ShortcutElement } from '../components/elements/shortcutElement.component';
import { ShortcutInputElement } from '../components/elements/shortcutInputElement.component';
import { SourceCaptionElement } from '../components/elements/sourceCaptionElement.component';
import { SuggestionLeaf } from '../components/elements/suggestionLeaf.component';
import { TableCellElement, TableCellHeaderElement } from '../components/elements/tableCellElement.component';
import { TableElement } from '../components/elements/tableElement.component';
import { TableRowElement } from '../components/elements/tableRowElement.component';
import { CitationFloatingToolbar } from '../components/toolbar/citationFloatingToolbar.component';
import { LinkFloatingToolbar } from '../components/toolbar/linkFloatingToolbar.component';
import { ATTACHMENT_HEADING_KEY, AttachmentHeadingPlugin } from '../plugins/attachmentHeadingPlugin';
import { commentsPlugin } from '../plugins/commentsPlugin';
import { suggestionPlugin } from '../plugins/suggestionPlugin';
import { Prompt_SystemBlockSelecting, Prompt_SystemDefault, Prompt_SystemSelecting, Prompt_UserBlockSelecting, Prompt_UserDefault, Prompt_UserSelecting } from '../prompts';
import { useAiDisclaimer } from './useAiDisclaimer.hook';
import { useUploadFileFunction } from './useUploadFile.hook';

const resetBlockTypesCommonRule = {
    defaultType: ParagraphPlugin.key,
    types: [
        ...HEADING_LEVELS,
        ATTACHMENT_HEADING_KEY,
        ListStyleType.Disc,
        ListStyleType.Decimal,
        IndentListPlugin.key,
        ImagePlugin.key,
        FigureCaptionPlugin.key,
        LinkPlugin.key
    ],
};

export const basicNodesPlugins = [
    HeadingPlugin.configure({ options: { levels: 5 } }),
    AttachmentHeadingPlugin,
    BasicMarksPlugin,
    HighlightPlugin,
    AlignPlugin.configure({
        inject: {
            targetPlugins: [
                ...HEADING_LEVELS,
                ATTACHMENT_HEADING_KEY,
                ParagraphPlugin.key,
                ImagePlugin.key,
                CaptionPlugin.key,
                FigureCaptionPlugin.key,
                SourceCaptionPlugin.key
            ],
            nodeProps: {
                defaultNodeValue: 'justify',
            },
        }
    }),
    TablePlugin.configure({
        options: {
            initialTableWidth: 600,
            disableMerge: false,
            disableMarginLeft: true
        },
    }),
    IndentPlugin.configure({
        options: {
            indentMax: 5
        },
        inject: {
            targetPlugins: [ParagraphPlugin.key],
        }
    }),
    IndentListPlugin.configure({
        inject: {
            targetPlugins: [ParagraphPlugin.key],
        }
    }),
    CaptionPlugin.configure({
        options: {
            plugins: [
                ImagePlugin
            ],
        },
    }),
    SourceCaptionPlugin,
    PlaceholderPlugin.configure({
        options: {
            disableEmptyPlaceholder: true,
            rules: [
                { mediaType: 'image/jpeg' },
                { mediaType: 'image/png' }
            ],
            uploadConfig: {
                image: {
                    maxFileSize: '2MB',
                    mediaType: 'img',
                }
            }
        },
        render: { afterEditable: MediaUploadToast },
    }),
    LinkPlugin.configure({
        options: {
            forceSubmit: true
        },
        render: { afterEditable: () => <LinkFloatingToolbar /> },
    }),
    SoftBreakPlugin.configure({
        options: {
            rules: [
                { hotkey: 'shift+enter' },
            ],
        },
    }),
    ExitBreakPlugin.configure({
        options: {
            rules: [
                {
                    hotkey: 'enter',
                    level: 1,
                    query: {
                        allow: [
                            ...HEADING_LEVELS,
                            ATTACHMENT_HEADING_KEY,
                            ImagePlugin.key,
                            FigureCaptionPlugin.key,
                            SourceCaptionPlugin.key,
                            LinkPlugin.key
                        ],
                        end: true,
                        start: true,
                    },
                    relative: true,
                },
            ],
        },
    }),
    ResetNodePlugin.configure({
        options: {
            rules: [
                {
                    ...resetBlockTypesCommonRule,
                    hotkey: 'Enter',
                    predicate: (editor) =>
                        editor.api.isEmpty(editor.selection, { block: true, text: true }),
                },
                {
                    ...resetBlockTypesCommonRule,
                    hotkey: 'Backspace',
                    predicate: (editor) => editor.api.isAt({ start: true }),
                },
            ],
        },
    }),
    // SelectOnBackspacePlugin.configure({
    //     options: {
    //         query: {
    //             allow: [
    //                 // ImagePlugin.key,
    //             ],
    //         },
    //     },
    // }),
    MarkdownPlugin.configure({ options: { indentList: true } }),
    JuicePlugin,
    DocxPlugin
] as const;

export interface TextEditorOptions {
    yjsProvider: HocuspocusProvider;
    document: DocumentV1.Record;
    documentField: string;
    documentFieldList: string[];
    getYjsDocumentNode: () => Y.XmlText;
    cursorData: CursorData;
    initialValue?: Partial<TElement>;
    preloadedContent?: TElement;
}

export function useTextEditor(opts: TextEditorOptions) {
    const { useAiFeature } = useAiDisclaimer();
    const uploadImage = useUploadFileFunction(opts.document.documentId);

    const docConfig = opts.document.template?.config as DocumentConfig;

    const editor = usePlateEditor({
        plugins: [
            ...basicNodesPlugins,
            CitationPlugin.configure({
                options: {
                    insertSpaceAfterCitation: true,
                    trigger: '@',
                    pageNumberPrefix: docConfig.citationPageNumberPrefix ?? ''
                },
                render: { afterEditable: () => <CitationFloatingToolbar /> },
            }),
            ShortcutPlugin.configure({ options: { insertSpaceAfterShortcut: true, trigger: '#' } }),
            ImagePlugin.configure({
                options: {
                    disableEmbedInsert: true,
                    disableUploadInsert: false,
                    uploadImage,
                }
            }),
            FigureCaptionPlugin.configure({
                options: {
                    captionPosition: {
                        image: docConfig.captionsPositionForImages ?? docConfig.captionsPosition ?? 'below',
                        table: docConfig.captionsPositionForTables ?? docConfig.captionsPosition ?? 'below',
                        chart: docConfig.captionsPositionForCharts ?? docConfig.captionsPosition ?? 'below',
                        formula: docConfig.captionsPosition ?? 'below',
                        map: docConfig.captionsPosition ?? 'below',
                        other: docConfig.captionsPosition ?? 'below',
                    }
                }
            }),
            FootnotesPlugin.configure({
                options: {
                    provider: opts.yjsProvider,
                    document: opts.document,
                    cursorData: opts.cursorData,
                    currentDocumentField: opts.documentField,
                    documentFieldList: opts.documentFieldList,
                    footnoteOffsetField: 'footnotesOffset',
                },
                render: {
                    afterEditable: BlockFootnote as any,
                }
            }),
            DndPlugin.configure({ options: { enableScroller: true } }),
            BlockSelectionPlugin.configure({
                render: {
                    belowRootNodes: (props) => {
                        if (!props.className?.includes('slate-selectable')) return null;
                        return <BlockSelection />;
                    },
                },
            }),
            suggestionPlugin.configure({
                options: {
                    currentUserId: opts.cursorData.identityId
                }
            }),
            commentsPlugin.configure({
                render: { aboveNodes: BlockDiscussion as any }
            }),
            YjsPlugin.configure({
                render: {
                    afterEditable: RemoteCursorOverlay,
                },
                options: {
                    cursorData: opts.cursorData,
                    provider: opts.yjsProvider,
                    getYjsDocumentNode: opts.getYjsDocumentNode,
                    initialValue: opts.initialValue,
                    preloadedContent: opts.preloadedContent,
                }
            }),
            AIPlugin,
            AIChatPlugin.configure({
                render: { afterEditable: () => <AIMenu /> },
                options: {
                    useFeature: useAiFeature,
                    systemTemplate({ isBlockSelecting, isSelecting }) {
                        return isBlockSelecting
                            ? Prompt_SystemBlockSelecting(opts)
                            : isSelecting
                                ? Prompt_SystemSelecting(opts)
                                : Prompt_SystemDefault(opts);
                    },
                    promptTemplate({ isBlockSelecting, isSelecting }) {
                        return isBlockSelecting
                            ? Prompt_UserBlockSelecting(opts)
                            : isSelecting
                                ? Prompt_UserSelecting(opts)
                                : Prompt_UserDefault(opts);
                    }
                }
            }),
            TrailingBlockPlugin.configure(() => ({
                options: {
                    level: 0,
                    type: 'p'
                }
            })),
            NodeIdPlugin
        ],
        override: {
            components: {
                [BoldPlugin.key]: withProps(PlateLeaf, { as: 'strong' }),
                [ItalicPlugin.key]: withProps(PlateLeaf, { as: 'em' }),
                [UnderlinePlugin.key]: withProps(PlateLeaf, { as: 'u' }),
                [SubscriptPlugin.key]: withProps(PlateLeaf, { as: 'sub' }),
                [SuperscriptPlugin.key]: withProps(PlateLeaf, { as: 'sup' }),
                [HighlightPlugin.key]: withProps(PlateLeaf, { as: 'mark', className: 'bg-yellow-200' }),
                [ParagraphPlugin.key]: withProps(PlateElement, {
                    as: 'div', // Must be rendered as div since it may contain other block elements (like lists)
                    className: 'mb-4 leading-relaxed text-justify',
                }),
                [HEADING_KEYS.h1]: ({ attributes, children, element, className, style }: SlateElementProps) => <h1 id={element.id as string} className={cn('text-3xl font-bold mb-4 mt-10 first:mt-0', className)} {...attributes} style={style}>{children}</h1>,
                [HEADING_KEYS.h2]: ({ attributes, children, element, className, style }: SlateElementProps) => <h2 id={element.id as string} className={cn('text-2xl font-semibold mb-2 mt-8', className)} {...attributes} style={style}>{children}</h2>,
                [HEADING_KEYS.h3]: ({ attributes, children, element, className, style }: SlateElementProps) => <h3 id={element.id as string} className={cn('text-xl font-bold mb-2 mt-6', className)} {...attributes} style={style}>{children}</h3>,
                [HEADING_KEYS.h4]: ({ attributes, children, element, className, style }: SlateElementProps) => <h4 id={element.id as string} className={cn('text-lg font-bold mb-2 mt-6', className)} {...attributes} style={style}>{children}</h4>,
                [HEADING_KEYS.h5]: ({ attributes, children, element, className, style }: SlateElementProps) => <h5 id={element.id as string} className={cn('text-base font-bold mb-2 mt-6', className)} {...attributes} style={style}>{children}</h5>,
                [ATTACHMENT_HEADING_KEY]: ({ attributes, children, element, className, style }: SlateElementProps) => <h2 id={element.id as string} className={cn('text-2xl font-semibold mb-2 mt-8', className)} {...attributes} style={style}>{children}</h2>,
                [BulletedListPlugin.key]: withProps(PlateElement, { as: 'ul', className: 'list-disc pl-6 mb-4 mt-2' }),
                [NumberedListPlugin.key]: withProps(PlateElement, { as: 'ol', className: 'list-decimal pl-6 mb-4 mt-2' }),
                [ListItemPlugin.key]: withProps(PlateElement, { as: 'li', className: 'mb-2' }),
                [CitationPlugin.key]: CitationElement,
                [CitationInputPlugin.key]: CitationInputElement,
                [ShortcutPlugin.key]: ShortcutElement,
                [ShortcutInputPlugin.key]: ShortcutInputElement,
                [ImagePlugin.key]: ImageElement,
                [PlaceholderPlugin.key]: MediaPlaceholderElement,
                [FigureCaptionPlugin.key]: FigureCaptionElement,
                [SourceCaptionPlugin.key]: SourceCaptionElement,
                [TableCellHeaderPlugin.key]: TableCellHeaderElement,
                [TableCellPlugin.key]: TableCellElement,
                [TablePlugin.key]: TableElement,
                [TableRowPlugin.key]: TableRowElement,
                [LinkPlugin.key]: LinkElement,
                [FootnotesPlugin.key]: FootnoteElement,
                [SuggestionPlugin.key]: SuggestionLeaf,
                [CommentsPlugin.key]: CommentLeaf,
                [AIPlugin.key]: AILeaf
            }
        }
    }, [ opts.documentField, opts.documentFieldList ]);

    return editor;
}
